import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { api } from '../../services/apiService';
import { safeImageDataUrl } from './guided-practice-section';
const DetailedGuidanceContent = lazy(() => import('../../components/detailed-guidance-content/detailed-guidance-content'));

const sameAnswer = (a, b) => typeof a === 'string' && typeof b === 'string' && a.trim().toLowerCase() === b.trim().toLowerCase();

function QuestionImage({ name }) {
  const [data, setData] = useState(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setData(null);
    Promise.resolve().then(() => api.getImage(name)).then(result => {
      if (active) setData(safeImageDataUrl(result?.imgData) || false);
    }).catch(() => { if (active) setData(false); });
    return () => { active = false; };
  }, [name, attempt]);
  if (data === null) return <p role="status">Loading question image…</p>;
  if (!data) return <p>Question image unavailable. <Button onClick={() => setAttempt(value => value + 1)}>Retry image</Button></p>;
  return <img className="psq-review-image" src={data} alt="Question illustration" />;
}

export default function PostSessionQuizReview({ row, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [index, setIndex] = useState(0);
  const heading = useRef(null);
  useEffect(() => {
    let active = true;
    setData(null); setError(false); setIndex(0);
    Promise.resolve().then(() => api.getTutorPostSessionQuizReview(row.rowKey)).then(result => {
      if (!active) return;
      if (result?.errMsg || result?.errStatus || !Array.isArray(result?.questions) || !result.questions.length
          || result.questions.some(item => !item?.question?.id || typeof item.question.stem !== 'string')) {
        setError(true);
      } else setData(result);
    }).catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [row.rowKey, attempt]);
  useEffect(() => { if (data) heading.current?.focus(); }, [data, index]);
  const item = data?.questions[index];
  const question = item?.question;
  const answered = item?.studentAnswer != null;
  const navigate = next => setIndex(next);
  return <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby="psq-review-title" className="psq-review">
    <DialogTitle id="psq-review-title">{data?.title || row.quizTitle}
      <span className="psq-review-subtitle">{row.studentDisplayName || row.studentEmail} · Read-only review</span>
    </DialogTitle>
    <DialogContent dividers>
      {!data && !error && <div role="status"><CircularProgress size={20} /> Loading quiz…</div>}
      {error && <div role="alert">Couldn’t load this quiz. Access may have changed, or the quiz may be temporarily unavailable.
        <Button onClick={() => setAttempt(value => value + 1)}>Retry</Button>
      </div>}
      {question && <>
        <div className="psq-review-context">
          <span>{data.completed ? 'Completed quiz' : 'Quiz preview'}</span>
          <span>Viewing this quiz does not change the student’s progress.</span>
        </div>
        <h3 ref={heading} tabIndex={-1} className="psq-review-heading">Question {index + 1} of {data.questions.length}</h3>
        <p className="psq-review-stem">{question.stem}</p>
        {(question.imageList || []).filter(name => typeof name === 'string' && name).map(name => <QuestionImage key={name} name={name} />)}
        <ol className="psq-review-options" type="A" aria-label="Answer choices">
          {(question.optionList || []).map((option, i) => <li key={i} className={sameAnswer(option, question.correctAnswer) ? 'psq-review-correct' : ''}>
            {option}{sameAnswer(option, question.correctAnswer) && <strong> — Correct answer</strong>}
            {answered && sameAnswer(option, item.studentAnswer) && <strong> — Student’s answer</strong>}
          </li>)}
        </ol>
        <div className="psq-review-answer">
          <p>{answered ? `Student’s answer: ${item.studentAnswer}` : 'No answer recorded'}</p>
          {answered && typeof item.correct === 'boolean' && <p>{item.correct ? 'Recorded as correct' : 'Recorded as incorrect'}</p>}
          <p>{question.correctAnswer?.trim() ? `Correct answer: ${question.correctAnswer}` : 'Answer key unavailable'}</p>
        </div>
        <h3>Explanation</h3>
        {question.briefExplanation || question.additionalExplanation ? <Suspense fallback={<p>Loading explanation…</p>}>
          <DetailedGuidanceContent briefExplanation={question.briefExplanation} additionalExplanation={question.additionalExplanation} />
        </Suspense> : <p>No explanation available for this question.</p>}
      </>}
    </DialogContent>
    <DialogActions className="psq-review-actions">
      <Button onClick={onClose}>Close quiz</Button>
      {data && <>
        <Button disabled={index === 0} onClick={() => navigate(index - 1)}>Previous question</Button>
        <Button color="primary" variant="contained" disabled={index === data.questions.length - 1}
          onClick={() => navigate(index + 1)}>Next question</Button>
      </>}
    </DialogActions>
  </Dialog>;
}
